import { MouseEvent, ReactElement } from 'react';

import Input from '@/common/Input';
import { ManifestsTypes } from '@/features/Manifests/types';
import Table from '@/common/Table';
import { getManifestTableGridTemplateColumns } from './utils';
import { useManifestsState } from '@/features/Manifests';

const ManifestTableHeader = (): ReactElement => {
  const [{ sortBy, sortDirection, allSelected, onSelectAll, settings }, setState] = useManifestsState(({ state, setState }) => [
    {
      sortBy: state.table.sorting.column,
      sortDirection: state.table.sorting.direction,
      allSelected: state.imports.length > 0 && state.selected.length === state.imports.length,
      onSelectAll: () =>
        setState((current) => ({
          ...current,
          selected: current.selected.length === current.imports.length ? [] : current.imports.map((imp) => imp.id),
        })),
      settings: state.settings,
    },
    setState,
  ]);

  const handleSort =
    (column: string) =>
    (event: MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      const isSameColumn = sortBy === column;
      let newDirection: 'asc' | 'desc' | undefined;
      if (!isSameColumn) {
        newDirection = 'asc';
      } else if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = undefined;
      } else {
        newDirection = 'asc';
      }
      setState((current) => ({
        ...current,
        table: {
          ...current.table,
          sorting: {
            column,
            direction: newDirection,
          },
        },
      }));
    };

  const sorting = (key: string): { direction: ManifestsTypes.Table['sorting']['direction']; onSort: (event: MouseEvent) => void } => ({
    direction: sortBy === key ? sortDirection : undefined,
    onSort: handleSort(key),
  });

  const gridTemplateColumns = getManifestTableGridTemplateColumns(settings);

  return (
    <Table.Row header style={{ gridTemplateColumns }}>
      {settings.columns.SELECT && (
        <Table.Cell className="SELECT">
          <Input type="checkbox" onClick={onSelectAll} checked={allSelected} />
        </Table.Cell>
      )}
      {settings.columns.DATE && (
        <Table.Cell className="DATE" sorting={sorting('createdAt')}>
          DATE
        </Table.Cell>
      )}
      {settings.columns.STATUS && (
        <Table.Cell className="STATUS" sorting={sorting('statusDisplayName')}>
          STATUS
        </Table.Cell>
      )}
      {settings.columns.PROVIDER && (
        <Table.Cell className="PROVIDER" sorting={sorting('provider.displayName')}>
          PROVIDER
        </Table.Cell>
      )}
      {settings.columns.IMPORTED_BY && (
        <Table.Cell className="IMPORTED-BY" sorting={sorting('importedBy')}>
          IMPORTED BY
        </Table.Cell>
      )}
      {settings.columns.FROM && (
        <Table.Cell className="FROM" sorting={sorting('email.from')}>
          FROM
        </Table.Cell>
      )}
      {settings.columns.SUBJECT && (
        <Table.Cell className="SUBJECT" sorting={sorting('email.subject')}>
          SUBJECT
        </Table.Cell>
      )}
      {settings.columns.TYPE && (
        <Table.Cell className="TYPE" sorting={sorting('type')}>
          TYPE
        </Table.Cell>
      )}
      {settings.columns.DOWNLOAD && (
        <Table.Cell className="DOWNLOAD">
          <i className="sv sv-download2 fs-4" />
        </Table.Cell>
      )}
    </Table.Row>
  );
};

export default ManifestTableHeader;
